import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

const COLLAPSED_HEIGHT = 46;
const ORDER_UPDATE_DELAY_MS = 500;
const LINKED_LINE_PROPERTY = '_linked_line';
const FOR_VARIANT_PROPERTY = '_for_variant';
const PARENT_PRODUCT_TITLE_PROPERTY = '_parent_product_title';
const FOR_PROPERTY = 'For';

class ProductGuaranteePicker extends HTMLElement {
  connectedCallback() {
    this.#content = this.querySelector('[data-guarantee-content]');
    this.#toggle = this.querySelector('[data-guarantee-toggle]');

    this.#toggle?.addEventListener('click', this.#onToggle);
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.#onSelect);
    });
    ThemeBus.addEventListener(EVENTS.cartUpdated, this.#onCartUpdated);

    this.#setExpanded(false);
  }

  disconnectedCallback() {
    if (this.#orderUpdateTimeout) {
      clearTimeout(this.#orderUpdateTimeout);
      this.#orderUpdateTimeout = null;
    }
    this.#clearExpandTransitionEnd();
    this.#toggle?.removeEventListener('click', this.#onToggle);
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.removeEventListener('change', this.#onSelect);
    });
    ThemeBus.removeEventListener(EVENTS.cartUpdated, this.#onCartUpdated);
  }

  #onCartUpdated = () => {
    this.#scheduleUpdateOrder();
  };

  #scheduleUpdateOrder() {
    if (this.#orderUpdateTimeout) {
      clearTimeout(this.#orderUpdateTimeout);
    }

    this.#orderUpdateTimeout = setTimeout(() => {
      this.#orderUpdateTimeout = null;
      if (this.isConnected) this.#updateOrder();
    }, ORDER_UPDATE_DELAY_MS);
  }

  #updateOrder() {
    if (!this.#content) return;
    const labels = [...this.#content.querySelectorAll('label')];
    const checkedLabel = this.querySelector('input[type="radio"]:checked')?.closest('label');
    let order = 0;
    labels.forEach((label) => {
      if (label === checkedLabel) {
        label.style.order = '-1';
      } else {
        label.style.order = String(order++);
      }
    });
  }

  #content = null;
  #toggle = null;
  #orderUpdateTimeout = null;
  #expandTransitionEnd = null;

  #onToggle = (event) => {
    event.preventDefault();
    if (this.dataset.expanded === 'true') {
      this.#setCollapsed(true);
    } else {
      this.#setExpanded();
    }
  };

  #clearExpandTransitionEnd() {
    if (!this.#expandTransitionEnd) return;
    this.removeEventListener('transitionend', this.#expandTransitionEnd);
    this.#expandTransitionEnd = null;
  }

  #onSelect = async (event) => {
    this.#scheduleUpdateOrder();

    const label = event.target.closest('label');
    const guaranteeVariantId = label?.dataset.productGuaranteeOption;

    if (!guaranteeVariantId) {
      await this.#removeGuarantee();
      return;
    }

    await this.#applyGuarantee(Number(guaranteeVariantId));
  };

  async #removeGuarantee() {
    const lineKey = this.dataset.itemKey;
    if (!lineKey) return;

    this.#setLoading(true);
    ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdating));
    let cartUpdated = false;

    try {
      const removed = await this.#removeExistingGuarantee(lineKey);
      if (removed) {
        ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
        cartUpdated = true;
      }
    } catch (error) {
      console.error('[GuaranteePicker] Failed to remove guarantee', error);
    } finally {
      this.#setLoading(false);
      if (!cartUpdated) {
        ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdateEnd));
      }
    }
  }

  async #applyGuarantee(guaranteeVariantId) {
    const lineKey = this.dataset.itemKey;
    if (!lineKey) return;

    this.#setLoading(true);
    ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdating));
    let cartUpdated = false;

    try {
      await this.#removeExistingGuarantee(lineKey);

      const quantity = Number(this.dataset.quantity) || 1;
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: guaranteeVariantId,
              quantity,
              properties: {
                [LINKED_LINE_PROPERTY]: lineKey,
                [FOR_VARIANT_PROPERTY]: this.dataset.productVariantId,
                [PARENT_PRODUCT_TITLE_PROPERTY]: this.dataset.productTitle || '',
                [FOR_PROPERTY]: this.dataset.productTitle || '',
              },
            },
          ],
        }),
      });

      const result = await response.json();

      if (result.status) {
        console.error('[GuaranteePicker]', result.description || result.message);
        return;
      }

      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
      cartUpdated = true;
    } catch (error) {
      console.error('[GuaranteePicker] Failed to add guarantee', error);
    } finally {
      this.#setLoading(false);
      if (!cartUpdated) {
        ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdateEnd));
      }
    }
  }

  async #removeExistingGuarantee(lineKey) {
    const cart = await this.#getCart();
    const productVariantId = this.dataset.productVariantId;
    const existing = cart.items.find((item) => {
      if (item.product_type !== 'Guarantee') return false;

      if (item.properties?.[LINKED_LINE_PROPERTY] === lineKey) return true;

      if (
        productVariantId &&
        String(item.properties?.[FOR_VARIANT_PROPERTY]) === String(productVariantId)
      ) {
        return true;
      }

      return false;
    });

    if (!existing) return false;

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: existing.key, quantity: 0 }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove existing guarantee');
    }

    return true;
  }

  async #getCart() {
    if (window.__cart?.items) return window.__cart;

    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart');

    const cart = await response.json();
    window.__cart = cart;
    return cart;
  }

  #setLoading(loading) {
    this.toggleAttribute('aria-busy', loading);
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.disabled = loading;
    });
    this.#toggle?.toggleAttribute('disabled', loading);
  }

  #animateMaxHeight(maxHeight) {
    this.style.maxHeight = `${this.offsetHeight}px`;
    this.offsetHeight;

    requestAnimationFrame(() => {
      this.style.maxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
    });
  }

  #setCollapsed(animate) {
    this.#clearExpandTransitionEnd();
    this.dataset.expanded = 'false';
    this.#toggle?.setAttribute('aria-expanded', 'false');
    this.#scheduleUpdateOrder();

    if (animate) {
      this.#animateMaxHeight(COLLAPSED_HEIGHT);
      return;
    }

    this.style.maxHeight = `${COLLAPSED_HEIGHT}px`;
  }

  #setExpanded(animate = true) {
    this.#clearExpandTransitionEnd();
    this.dataset.expanded = 'true';
    this.#toggle?.setAttribute('aria-expanded', 'true');
    this.#scheduleUpdateOrder();

    if (!animate) {
      this.style.maxHeight = 'none';
      return;
    }

    const fullHeight = this.scrollHeight;
    this.#animateMaxHeight(fullHeight);

    this.#expandTransitionEnd = (event) => {
      if (event.target !== this || event.propertyName !== 'max-height') return;
      if (this.dataset.expanded !== 'true') return;
      this.style.maxHeight = 'none';
      this.#clearExpandTransitionEnd();
    };
    this.addEventListener('transitionend', this.#expandTransitionEnd);
  }
}

customElements.define('product-guarantee-picker', ProductGuaranteePicker);
